import React, { useCallback, useEffect, useState } from 'react';
import axiosInstance from 'config/axiosInstance';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { businessDateRange, businessZoneQuery, toBusinessDate } from 'config/time';
import { ALCOHOL_EXCEED_THRESHOLD, EXCEED_FILTER_PARAM } from 'config/measurement';
import alcohol_limit from '../../assets/icons/dashboard/alcohol_limit.svg';
import driver_submit from '../../assets/icons/dashboard/driver_submit.svg';
import total_device from '../../assets/icons/dashboard/device.svg';
import alert_icon from '../../assets/icons/dashboard/emergency.svg';
import dot from '../../assets/icons/dashboard/dot.svg';
import { useTranslation } from 'react-i18next';

const SubmissionsStatus: React.FC = () => {
  // BE-27-AC3。未取得・取得失敗は null とし、0件（安全）として表示しない。
  const [exceedAlcoholLimits, setExceedAlcoholLimits] = useState<number | null>(
    null
  );
  const [totalDriverSubmitted, setTotalDriverSubmitted] = useState<
    number | null
  >(null);
  const [totalDriverNotSubmitted, setTotalDriverNotSubmitted] = useState<
    number | null
  >(null);
  const [totalDevice, setTotalDevice] = useState<number | null>(null);
  const [deviceUse, setDeviceUse] = useState<number | null>(null);
  // ADM-27。カードの文言は「本日の報告状況」なので、集計も当日にする。
  // 以前は当月を集計しながら日次の見出しを付けていた。
  const businessDay = toBusinessDate(new Date());

  const { t } = useTranslation('dashboard');
  const beginRequest = useLatestRequest();
  const [errors, setErrors] = useState<unknown[]>([]);

  const fetchDriverSubmitted = useCallback(async () => {
    const { gte, lte } = businessDateRange(businessDay, businessDay);
    // EV-493。報告した人数・未報告の人数はサーバーが数える（1回の要求）。以前は recent_submission を
    // 100件ずつ最後のページまで読み、期間の測定がある運転者を画面で数えていた（ADM-09）。
    const response = await axiosInstance.get<{ submitted: number; not_submitted: number }>(
      `/v1/drivers/submission_counts/` +
        // RV-278・RV-279。画面の境界の計算と同じ時間帯を time_zone で渡す。
        `?${businessZoneQuery()}alcoholtest__testing_time__gte=${encodeURIComponent(gte ?? '')}` +
        `&alcoholtest__testing_time__lte=${encodeURIComponent(lte ?? '')}`
    );

    return { submittedCount: response.data.submitted, notSubmittedCount: response.data.not_submitted };
  }, [businessDay]);

  const fetchData = useCallback(async (reset = false) => {
    const isCurrent = beginRequest();
    setErrors([]);
    // 対象日変更は旧値を消す。同じ日の再試行は成功済みの値を保持する。
    if (reset) {
      setExceedAlcoholLimits(null);
      setTotalDriverSubmitted(null);
      setTotalDriverNotSubmitted(null);
      setTotalDevice(null);
      setDeviceUse(null);
    }
    // 1つの取得が失敗しても、他のカードは表示する。失敗したカードは未取得のまま。
    const [alcohol, driver, device] = await Promise.allSettled([
      fetchExceedAlcoholLimitData(),
      fetchDriverSubmitted(),
      fetchDevice(),
    ]);

    if (!isCurrent()) return;
    setErrors([alcohol, driver, device].map(result => result.status === 'rejected' ? result.reason : null));
    if (alcohol.status === 'fulfilled') setExceedAlcoholLimits(alcohol.value);
    if (driver.status === 'fulfilled') {
      setTotalDriverSubmitted(driver.value.submittedCount);
      setTotalDriverNotSubmitted(driver.value.notSubmittedCount);
    }
    if (device.status === 'fulfilled') {
      setTotalDevice(device.value.totalDevice);
      setDeviceUse(device.value.deviceUseCount);
    }
    [alcohol, driver, device].forEach((result) => {
      if (result.status === 'rejected') {
        console.error('Failed to fetch data:', result.reason);
      }
    });
  }, [fetchDriverSubmitted, beginRequest]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const fetchExceedAlcoholLimitData = async () => {
    // ADM-26・TH-AC3。超過の判定はサーバーと同じ「閾値以上」（0.15以上）に揃える。
    // ADM-27-AC3。期間の条件は付けず全期間を数える。見出しにも「全期間」と出す
    // （隣の「本日報告したドライバー数」と期間が違うため。当日に揃えるかは判断待ち）。
    const apiUrl = `/v1/alcohol_tests/?${EXCEED_FILTER_PARAM}`;
    const response = await axiosInstance.get(apiUrl);
    return response.data.count;
  };

  const fetchDevice = async () => {
    // EV-493。機器の行は要らないので、件数（count）だけを読む（1件だけのページ）。
    // 以前は全件（100件ずつ最大50回）を取ってきて、user のある行を数えていた。
    const [all, used] = await Promise.all([
      axiosInstance.get<{ count: number }>('/v1/devices/?page_size=1'),
      axiosInstance.get<{ count: number }>('/v1/devices/?user__isnull=false&page_size=1'),
    ]);

    return { totalDevice: all.data.count, deviceUseCount: used.data.count };
  };

  return (
    <section className="flex flex-row space-x-4 justify-between">
      {renderStatCard(
        alcohol_limit,
        exceedAlcoholLimits,
        t('status.alcohol-limit.info-title'),
        `${t('status.alcohol-limit.limit', {
          threshold: ALCOHOL_EXCEED_THRESHOLD.toFixed(2),
        })} ${t(
          'status.alcohol-limit.limit2'
        )}`,
        alert_icon,
        errors[0] ? <FetchFailed error={errors[0]} onRetry={() => fetchData(false)} message={t('ui.detail-fetch-failed', { ns: 'common' })} /> : undefined
      )}
      {renderStatCard(
        driver_submit,
        totalDriverSubmitted,
        // ADM-27。数えているのは「本日報告した人数」。対象日も示す。
        t('status.driver-submitted.info-title', { date: businessDay }),
        `${t('status.driver-submitted.limit')} ${formatCount(totalDriverNotSubmitted)}  ${t(
          'status.driver-submitted.limit2'
        )}`,
        alert_icon,
        errors[1] ? <FetchFailed error={errors[1]} onRetry={() => fetchData(false)} message={t('ui.detail-fetch-failed', { ns: 'common' })} /> : undefined
      )}
      {renderStatCard(
        total_device,
        totalDevice,
        t('status.total-device.info-title'),
        `${formatCount(deviceUse)} ${t('status.total-device.limit')}`,
        dot,
        errors[2] ? <FetchFailed error={errors[2]} onRetry={() => fetchData(false)} message={t('ui.detail-fetch-failed', { ns: 'common' })} /> : undefined
      )}
    </section>
  );
};

/** 未取得・取得失敗の件数は「—」と表示する。0件と区別するため。 */
const formatCount = (count: number | null) =>
  count === null ? '—' : String(count);

const renderStatCard = (
  icon: string,
  count: number | null,
  title: string,
  subtitle: string,
  customIcon?: string,
  error?: React.ReactNode
) => (
  <div className="flex flex-1 flex-col bg-white px-3 py-2 rounded-md">
    <div className="flex flex-row justify-between">
      <p className="text-primary/300 text-h5 font-semi-bold">
        {formatCount(count)}
      </p>
      <img src={icon ?? customIcon} alt="" />
    </div>
    {error}
    <div className="flex flex-col">
      <p className="text-body-2">{title}</p>
      <div className="flex flex-row space-x-2">
        <img src={icon ? customIcon : alert_icon} alt="" />
        <p className="text-gray-500 text-caption">{subtitle}</p>
      </div>
    </div>
  </div>
);

export default SubmissionsStatus;
